"use client"

import { AlertTriangle } from "@untitledui/icons"
import {
  DialogTrigger as AriaDialogTrigger,
  Heading as AriaHeading,
} from "react-aria-components"

import { BackgroundPattern } from "@/components/ui/background-pattern"
import { Button } from "@/components/ui/button"
import { CloseButton } from "@/components/ui/close-button"
import { FeaturedIcon } from "@/components/ui/featured-icon"
import { Dialog, Modal, ModalOverlay } from "@/components/ui/modal"

export interface WarningStackedLeftAlignedModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  onConfirm: () => void
  confirmLabel?: string
  children?: React.ReactNode
}

/**
 * WarningStackedLeftAlignedModal component.
 * The modal has exclamation-mark icon and a confirm button in yellow theme.
 */
export function WarningStackedLeftAlignedModal({
  isOpen,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmLabel = "Continue",
  children,
}: WarningStackedLeftAlignedModalProps) {
  const handleConfirm = () => {
    onConfirm()
    onOpenChange(false)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <AriaDialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalOverlay isDismissable>
        <Modal>
          <Dialog>
            <div className="bg-primary relative w-full overflow-hidden rounded-2xl shadow-xl sm:max-w-100">
              <CloseButton
                onClick={handleCancel}
                theme="light"
                size="lg"
                className="absolute top-3 right-3"
              />
              <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
                <div className="relative w-max">
                  <FeaturedIcon
                    color="warning"
                    size="lg"
                    theme="light"
                    icon={AlertTriangle}
                  />
                  <BackgroundPattern
                    pattern="circle"
                    size="sm"
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  />
                </div>
                <div className="z-10 flex flex-col gap-0.5">
                  <AriaHeading
                    slot="title"
                    className="text-md text-primary font-semibold"
                  >
                    {title}
                  </AriaHeading>
                  <p className="text-tertiary text-sm">{description}</p>
                </div>
              </div>
              {children && (
                <div className="z-10 px-4 pt-4 sm:px-6">{children}</div>
              )}
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="lg" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button color="primary" size="lg" onClick={handleConfirm}>
                  {confirmLabel}
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </AriaDialogTrigger>
  )
}
